/**
 * FieldSalienceDecoration — per-paragraph anti-Echo cues from field_map.
 *
 * Phase 2.5 of the Fieldmap-Whiteboard initiative (Post #777). Pairs
 * with the corner-badge `SalienceOverlay` to surface the same signals
 * inline, at the source of the question / inside the dominated
 * section, so linear-reading bias doesn't bury them.
 *
 * Pattern mirrors `PendingDraftDecoration`: a transient ProseMirror
 * plugin whose state is mutated only via meta-tagged transactions,
 * never via CRDT writes. `field_map` data is a derived metric — it
 * does not belong in the Y.Doc, only as a UI overlay.
 *
 * Two CSS classes are dispatched onto matching paragraphs:
 *   - `tension-paragraph` — block carries an open question
 *   - `dominated-paragraph` — block lives in a section one author has
 *     crowded out (n_authors >= 2 and dominance_score >= 0.7)
 *
 * Styling lives in `styles/blockTypes.css`.
 *
 * External React code pushes updates via `setFieldSalience(view, ...)`
 * once per field_map tick. Since field_map polling is 7s and the doc
 * is unlikely to grow many paragraphs between ticks, recomputing
 * decorations only on data refresh (not on every transaction) is the
 * right cost trade.
 */

import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import type { EditorView } from '@tiptap/pm/view'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

interface SalienceState {
  tensions: Set<number>
  dominated: Set<number>
}

const PLUGIN_KEY = new PluginKey<SalienceState>('fieldSalienceDecoration')
const SET_META = 'fieldSalienceDecoration:set'

const EMPTY_STATE: SalienceState = {
  tensions: new Set(),
  dominated: new Set(),
}

function computeDecorations(
  doc: import('@tiptap/pm/model').Node,
  state: SalienceState,
): DecorationSet {
  if (state.tensions.size === 0 && state.dominated.size === 0) {
    return DecorationSet.empty
  }
  const decos: Decoration[] = []
  let paragraphIndex = 0
  doc.descendants((node, pos) => {
    if (node.type.name !== 'paragraph') return true
    const isTension = state.tensions.has(paragraphIndex)
    const isDominated = state.dominated.has(paragraphIndex)
    if (isTension || isDominated) {
      const classes: string[] = []
      if (isTension) classes.push('tension-paragraph')
      if (isDominated) classes.push('dominated-paragraph')
      decos.push(
        Decoration.node(pos, pos + node.nodeSize, {
          class: classes.join(' '),
        }),
      )
    }
    paragraphIndex += 1
    return false
  })
  return DecorationSet.create(doc, decos)
}

export const FieldSalienceDecoration = Extension.create({
  name: 'fieldSalienceDecoration',

  addProseMirrorPlugins() {
    return [
      new Plugin<SalienceState>({
        key: PLUGIN_KEY,
        state: {
          init: () => EMPTY_STATE,
          apply: (tr, prev) => {
            const next = tr.getMeta(SET_META) as SalienceState | undefined
            if (next) return next
            return prev
          },
        },
        props: {
          decorations(state) {
            const sal = PLUGIN_KEY.getState(state)
            if (!sal) return DecorationSet.empty
            return computeDecorations(state.doc, sal)
          },
        },
      }),
    ]
  },
})

/**
 * Push a new salience snapshot to the editor. Idempotent — same
 * payload reapplied is cheap (decoration recompute walks the doc but
 * sets nothing if no paragraph changed class). Call from a React
 * effect that subscribes to useFieldMap.
 */
export function setFieldSalience(
  view: EditorView,
  tensions: Set<number>,
  dominated: Set<number>,
) {
  const tr = view.state.tr.setMeta(SET_META, { tensions, dominated })
  view.dispatch(tr)
}
